import { supabase } from '../../lib/supabase'
import { DEFAULT_TOUR_OF_DUTY_HOURS, periodStartOf, type HeldQualification, type ScheduledDay } from './eligibility'

// What the eligibility warnings read for a set of agents and dates: the hours
// each is scheduled in the pay periods those dates fall in, their tour of duty,
// the qualifications they hold, and which assignments ask for one. Row-level
// security limits all of it to the supervisor's own teams.
export type EligibilityFacts = {
  tourHours: (employeeId: string) => number
  // The agent's scheduled hours by day over the pay periods asked for, and the
  // assignment on each entry.
  scheduled: (employeeId: string) => ScheduledDay[]
  entriesOn: (employeeId: string, date: string) => { hours: number; assignmentId?: string }[]
  held: (employeeId: string) => HeldQualification[]
  requiredFor: (assignmentId: string | null | undefined) => { qualificationId: string; name: string } | undefined
}

const addDays = (date: string, days: number) => {
  const [year, month, day] = date.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10)
}

type EntryRow = { employee_id: string; date: string; scheduled_hours: number | string | null; assignment_id: string | null }

export async function getEligibilityFacts(employeeIds: string[], dates: string[]): Promise<EligibilityFacts> {
  const ids = Array.from(new Set(employeeIds))
  const periodStarts = Array.from(new Set(dates.map(periodStartOf))).sort()
  const empty: EligibilityFacts = {
    tourHours: () => DEFAULT_TOUR_OF_DUTY_HOURS,
    scheduled: () => [],
    entriesOn: () => [],
    held: () => [],
    requiredFor: () => undefined,
  }
  if (ids.length === 0 || periodStarts.length === 0) return empty

  const [employees, entries, qualifications, assignments, catalog] = await Promise.all([
    supabase.from('employees').select('id, tour_of_duty_hours').in('id', ids),
    supabase
      .from('schedule_entries')
      .select('employee_id, date, scheduled_hours, assignment_id')
      .in('employee_id', ids)
      .gte('date', periodStarts[0])
      .lte('date', addDays(periodStarts[periodStarts.length - 1], 13)),
    supabase.from('employee_qualifications').select('employee_id, qualification_id, effective_from, effective_to').in('employee_id', ids),
    supabase.from('assignments').select('id, required_qualification_id'),
    supabase.from('qualifications').select('id, name'),
  ])

  const tour = new Map(((employees.data ?? []) as { id: string; tour_of_duty_hours: number | string | null }[]).map((row) => [row.id, Number(row.tour_of_duty_hours ?? DEFAULT_TOUR_OF_DUTY_HOURS)]))
  const rows = ((entries.data ?? []) as EntryRow[]).map((row) => ({
    employeeId: row.employee_id,
    date: row.date,
    hours: Number(row.scheduled_hours ?? 0),
    assignmentId: row.assignment_id ?? undefined,
  }))
  const held = new Map<string, HeldQualification[]>()
  for (const row of (qualifications.data ?? []) as { employee_id: string; qualification_id: string; effective_from: string; effective_to: string | null }[]) {
    held.set(row.employee_id, [...(held.get(row.employee_id) ?? []), { qualificationId: row.qualification_id, effectiveFrom: row.effective_from, effectiveTo: row.effective_to ?? undefined }])
  }
  const names = new Map(((catalog.data ?? []) as { id: string; name: string }[]).map((row) => [row.id, row.name]))
  const required = new Map<string, { qualificationId: string; name: string }>()
  for (const row of (assignments.data ?? []) as { id: string; required_qualification_id: string | null }[]) {
    if (row.required_qualification_id) required.set(row.id, { qualificationId: row.required_qualification_id, name: names.get(row.required_qualification_id) ?? 'required' })
  }

  return {
    tourHours: (employeeId) => tour.get(employeeId) ?? DEFAULT_TOUR_OF_DUTY_HOURS,
    scheduled: (employeeId) => rows.filter((row) => row.employeeId === employeeId).map(({ date, hours }) => ({ date, hours })),
    entriesOn: (employeeId, date) => rows.filter((row) => row.employeeId === employeeId && row.date === date).map(({ hours, assignmentId }) => ({ hours, assignmentId })),
    held: (employeeId) => held.get(employeeId) ?? [],
    requiredFor: (assignmentId) => (assignmentId ? required.get(assignmentId) : undefined),
  }
}
